import React, { useRef, useState } from 'react';
import { Avatar, Button, CircularProgress, Grid, useTheme } from '@mui/material';
import useAlert from '../../hooks/useAlert';
import useAuth from '../../hooks/useAuth';
import { useMutation } from '@apollo/client';
import { updateUser as updateUserMutation } from 'vendor-marketplace-shared-library/compiled/graphql/procure/procure-queries.graphql';
import AttachmentService from '../../services/attachment.service';
import {
  ERROR_SAVING_PROFILE_IMAGE,
  ERROR_UPLOADING_PROFILE_IMAGE,
  ONLY_IMAGE_UPLOAD,
} from '../../error-messages';
import useMixpanel from '../../hooks/useMixpanel';
import { MIXPANEL_EVENTS } from 'vendor-marketplace-shared-library';

export default function ProfilePictureUploader() {
  const alert = useAlert();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const auth = useAuth();
  const theme = useTheme();
  const { mixpanelTrack } = useMixpanel();
  const [loading, setLoading] = useState(false);

  const [updateUser] = useMutation(updateUserMutation, {
    refetchQueries: ['me'],
    awaitRefetchQueries: true,
    onError: () => {
      alert.setAlert({ severity: 'error', message: 'UPDATE_USER' });
      setLoading(false);
    },
    onCompleted: async () => {
      setLoading(false);
    },
  });

  const uploadAttachment = async (file: File) => {
    try {
      mixpanelTrack(MIXPANEL_EVENTS.PROFILE_PICTURE_UPLOAD_ATTEMPTED);
      const attachmentId = await AttachmentService.fileUpload(file);
      mixpanelTrack(MIXPANEL_EVENTS.PROFILE_PICTURE_UPLOAD_COMPLETED);
      return attachmentId;
    } catch (err) {
      alert.setAlert({ severity: 'error', message: ERROR_UPLOADING_PROFILE_IMAGE });
    }
  };

  const onSubmit = async (file: File) => {
    try {
      setLoading(true);
      const attachmentId = await uploadAttachment(file);
      await updateUser({
        variables: {
          input: {
            profilePictureAttachmentId: attachmentId,
          },
        },
      });
    } catch (err) {
      alert.setAlert({ severity: 'error', message: ERROR_SAVING_PROFILE_IMAGE });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      // only allow for image upload
      const regex = /\bimage\b/i;
      if (!regex.test(event.target.files[0].type)) {
        alert.setAlert({ severity: 'error', message: ONLY_IMAGE_UPLOAD });
        return;
      }
      await onSubmit(event.target.files[0]);
    }
  };

  return (
    <Grid container spacing={2} sx={styles.mainContainer}>
      <Grid item>
        {loading || auth.userQuery.loading ? (
          <Grid
            container
            sx={[styles.subContainer, { borderColor: theme.palette.secondary.light }]}
          >
            <Grid item>
              <CircularProgress />
            </Grid>
          </Grid>
        ) : (
          <Avatar
            alt={auth.userQuery.data?.me.firstName || ''}
            src={auth.userQuery.data?.me.profilePictureAttachment?.url || ''}
            sx={{ width: 125, height: 125 }}
          />
        )}
      </Grid>
      <Grid item>
        <Button size='small' variant='outlined' onClick={() => fileInputRef.current?.click()}>
          Upload profile photo
        </Button>
      </Grid>
      {/*File upload input*/}
      <input
        type='file'
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelection}
      />
    </Grid>
  );
}

const styles = {
  mainContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  subContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderStyle: 'solid',
    width: 125,
    height: 125,
    borderRadius: '50%',
  },
};
